import { type ReactNode, useMemo } from "react";

import { trpc } from "@blis/trpc/react";

import { Text } from "@/components/ui/text";
import { cn } from "@/components/ui/utils";
import type { TestRunDetails } from "../types";
import { getProgressValuesFromCounts } from "../utils";
import { TestProgressBar } from "./TestProgressBar";
import { TestRunCard } from "./TestRunCard";

interface TestRunProgressProps {
  run: TestRunDetails;
}

function TestStatus({
  variant,
  count,
  children,
}: {
  variant: "inProgress" | "validating" | "completed";
  count: number;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div
          className={cn(
            "h-2 w-2 rounded-full",
            variant === "inProgress" && "bg-yellow-500",
            variant === "validating" && "bg-blue-500",
            variant === "completed" && "bg-green-500",
          )}
        />
        <Text muted>{children}</Text>
      </div>
      <Text>{count}</Text>
    </div>
  );
}

export function TestRunProgress({ run }: TestRunProgressProps) {
  const { data: counts } = trpc.runs.counts.useQuery({ id: run.id });
  const values = useMemo(
    () =>
      getProgressValuesFromCounts(
        counts ?? { completedCount: 0, validatingCount: 0, pendingCount: 0 },
      ),
    [counts],
  );

  return (
    <TestRunCard
      title={
        <>
          <div className="flex items-center justify-between pb-4">
            <Text size="4">Progress</Text>
            <Text muted>
              {values.completed}/{values.total}
            </Text>
          </div>
          <TestProgressBar {...values} />
        </>
      }
    >
      <div className="flex flex-col gap-2">
        <TestStatus variant="inProgress" count={values.inProgress}>
          In Progress
        </TestStatus>
        <TestStatus variant="validating" count={values.validating}>
          Validating
        </TestStatus>
        <TestStatus variant="completed" count={values.completed}>
          Completed
        </TestStatus>
      </div>
    </TestRunCard>
  );
}
